import React, { useMemo, useState } from 'react'
import { FlexColumn } from '@/atoms/FlexContainers'
import { StarFilledIcon } from '@/atoms/Icons/StarFilledIcon'
import { StarOutlineIcon } from '@/atoms/Icons/StarOutlineIcon'
import { Text } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { VenueLogo } from '@/views/TicketCheckoutViews/components/VenueLogo'
import { TheaterPreferencesVenue } from '../types'
import { formatDistance } from '../utils'

interface VenueSelectionListProps {
  venues: TheaterPreferencesVenue[]
  loading: boolean
  error: boolean
  onVenueSelect?: (venueId: string, venueData?: TheaterPreferencesVenue) => void
  selectedVenueId?: string
}

export const VenueSelectionList: React.FC<VenueSelectionListProps> = ({
  venues,
  loading,
  error,
  onVenueSelect,
  selectedVenueId,
}) => {
  const { t } = useTranslate('tickets')
  const [localSelectedVenueId, setLocalSelectedVenueId] = useState<string | undefined>(selectedVenueId)

  // Sort venues by distance
  const sortedVenues = useMemo(() => {
    // Get normalized distance (handling potential unit differences)
    const getNormalizedDistance = (distance: number | undefined) => {
      if (distance === undefined) return Infinity
      return distance > 1000 ? distance * 0.000621371 : distance
    }

    // Sort by closest first
    return venues.toSorted((a, b) => {
      // Handle undefined distances (put them at the end)
      if (a.distance === undefined) return 1
      if (b.distance === undefined) return -1

      // Compare using normalized distances
      return getNormalizedDistance(a.distance) - getNormalizedDistance(b.distance)
    })
  }, [venues])

  const handleVenueSelect = (venueId: string, venue: TheaterPreferencesVenue) => {
    // Toggle selection if clicking on already selected venue
    if (venueId === localSelectedVenueId) {
      setLocalSelectedVenueId(undefined)
      if (onVenueSelect) {
        onVenueSelect('')
      }
    } else {
      setLocalSelectedVenueId(venueId)
      if (onVenueSelect) {
        onVenueSelect(venueId, venue)
      }
    }
  }

  if (loading) {
    return (
      <FlexColumn className="h-full min-h-0 gap-2 overflow-y-auto">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex h-20 shrink-0 flex-col rounded-xl border border-core-gray-100 bg-white">
            <div className="flex h-full items-start gap-2 p-3">
              <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-core-gray-200" />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div className="flex items-start justify-between">
                  <div className="h-4 w-32 animate-pulse rounded bg-core-gray-200" />
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-12 animate-pulse rounded bg-core-gray-200" />
                    <div className="h-5 w-5 animate-pulse rounded bg-core-gray-200" />
                  </div>
                </div>
                <div className="h-3 w-48 animate-pulse rounded bg-core-gray-200" />
                <div className="h-3 w-24 animate-pulse rounded bg-core-gray-200" />
              </div>
            </div>
          </div>
        ))}
      </FlexColumn>
    )
  }

  if (error) {
    return (
      <FlexColumn className="flex items-center justify-center overflow-hidden">
        <Text className="text-error-600">
          {t('venueListError', 'Unable to load theaters. Please try again later.')}
        </Text>
      </FlexColumn>
    )
  }

  if (!sortedVenues.length) {
    return (
      <FlexColumn className="items-center justify-center overflow-hidden">
        <Text className="text-core-gray-600">{t('noVenuesFound', 'No theaters found in this area.')}</Text>
      </FlexColumn>
    )
  }

  return (
    <FlexColumn className="h-full min-h-0 gap-2 overflow-y-auto">
      {sortedVenues.map((venue) => {
        const isSelected = venue.providerId === (selectedVenueId || localSelectedVenueId)
        return (
          <div
            key={venue.providerId}
            className={`flex h-20 shrink-0 cursor-pointer flex-col rounded-xl border ${
              isSelected ? 'border-info-400 bg-info-100' : 'border-transparent'
            }`}
            onClick={() => {
              handleVenueSelect(venue.providerId || '', venue)
            }}
          >
            <div className="flex h-full items-start gap-2 p-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center">
                {venue.vendorLogo?.cloudinaryPath ? (
                  <VenueLogo className="h-8 w-8" cloudinaryPath={venue.vendorLogo.cloudinaryPath} />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-core-gray-200">
                    <svg
                      className="h-5 w-5 text-core-gray-500"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                <div className="flex items-start justify-between">
                  <Text className="truncate text-sm font-semibold">{venue.name}</Text>
                  <div className="flex items-center gap-2">
                    {venue.distance !== undefined && (
                      <Text className="shrink-0 whitespace-nowrap text-xs text-core-gray-500">
                        {formatDistance(venue.distance)}
                      </Text>
                    )}
                    {isSelected ? (
                      <StarFilledIcon size={24} color={'warning-500'} />
                    ) : (
                      <StarOutlineIcon size={24} color={'core-gray-400'} />
                    )}
                  </div>
                </div>
                <Text className="mt-0.5 truncate text-xs text-core-gray-600">{venue.addressText}</Text>
                {venue.phone && <Text className="mt-0.5 truncate text-xs text-core-gray-600">{venue.phone}</Text>}
              </div>
            </div>
          </div>
        )
      })}
    </FlexColumn>
  )
}
